'use client';

import type { ReactNode } from 'react';
import { BookOpenIcon, TrashSimpleIcon } from '@phosphor-icons/react';
import { cn } from '../utils/cn';

export interface ProductLineItemProps {
  title: string;
  /** URL de la imagen; para un control total (next/image), usar `image`. */
  imageUrl?: string;
  /** Slot de imagen custom: reemplaza al <img> por defecto. */
  image?: ReactNode;
  href?: string;
  onNavigate?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Docente o meta secundaria, truncada a una línea. */
  subtitle?: string;
  /** Precio ya formateado (cada consumidor formatea con su moneda). */
  price?: ReactNode;
  /** Precio original tachado, ya formateado. */
  originalPrice?: ReactNode;
  /** Badge de descuento, ej. «−17%». */
  discountBadge?: string;
  /** Nota bajo el precio: cupos, cantidad, etc. */
  note?: ReactNode;
  /**
   * `md` es la fila de carrito (imagen 72px, título a dos líneas); `sm` la de
   * resumen (imagen 40px, todo a una línea).
   */
  size?: 'sm' | 'md';
  onRemove?: () => void;
  removeLabel?: string;
  /** Estilos del contenedor para variantes del consumidor (campañas, estados). */
  className?: string;
}

/**
 * Fila compacta de producto — la única variante transaccional del sitio
 * (carrito, resumen de compra, sidebars de campaña, dashboard). El precio va
 * debajo del título y el docente, nunca en columna aparte: no compite con el
 * título en pantallas chicas.
 */
export function ProductLineItem({
  title,
  imageUrl,
  image,
  href,
  onNavigate,
  subtitle,
  price,
  originalPrice,
  discountBadge,
  note,
  size = 'md',
  onRemove,
  removeLabel,
  className,
}: ProductLineItemProps) {
  const small = size === 'sm';

  const media = image ?? (
    <div
      className={cn(
        'rounded-adipa-md bg-adipa-surface-muted relative shrink-0 overflow-hidden',
        small ? 'h-10 w-10' : 'h-18 w-18',
      )}
    >
      {imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img alt="" className="absolute inset-0 h-full w-full object-cover" src={imageUrl} />
      ) : (
        <div className="bg-adipa-surface-lavender absolute inset-0 flex items-center justify-center">
          <BookOpenIcon aria-hidden="true" className="text-adipa-primary h-5 w-5 opacity-60" />
        </div>
      )}
    </div>
  );

  const heading = href ? (
    <a
      className={cn(
        'text-adipa-text-primary duration-adipa-fast hover:text-adipa-primary font-bold transition-colors',
        'line-clamp-2',
        small ? 'text-[12.5px] leading-4' : 'text-adipa-cart-title leading-4',
      )}
      href={href}
      onClick={onNavigate}
    >
      {title}
    </a>
  ) : (
    <span
      className={cn(
        'text-adipa-text-primary font-bold',
        'line-clamp-2',
        small ? 'text-[12.5px] leading-4' : 'text-adipa-cart-title leading-4',
      )}
    >
      {title}
    </span>
  );

  return (
    <article
      className={cn(
        'rounded-adipa-lg border-adipa-border bg-adipa-surface duration-adipa-fast hover:border-adipa-border-strong grid items-start gap-2.5 border transition-colors',
        small
          ? 'grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center p-2'
          : 'min-h-23 grid-cols-[4.5rem_minmax(0,1fr)_1.5rem] p-2.5',
        className,
      )}
    >
      {media}

      <div className={cn('flex min-w-0 flex-col', small ? 'justify-center gap-0' : 'gap-0.5')}>
        {heading}

        {subtitle ? (
          <p className="text-adipa-cart-meta text-adipa-text-secondary truncate leading-4">
            {subtitle}
          </p>
        ) : null}

        {price !== undefined ? (
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <span className="text-adipa-cart-price text-adipa-text-primary font-bold tabular-nums">
              {price}
            </span>
            {originalPrice ? (
              <span className="text-adipa-cart-meta text-adipa-text-secondary whitespace-nowrap tabular-nums line-through">
                {originalPrice}
              </span>
            ) : null}
            {discountBadge ? (
              <span className="bg-adipa-secondary text-adipa-primary-foreground rounded-adipa-full inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold">
                {discountBadge}
              </span>
            ) : null}
          </div>
        ) : null}

        {note ? <div className="mt-0.5">{note}</div> : null}
      </div>

      {onRemove ? (
        <button
          aria-label={removeLabel ?? `Quitar ${title}`}
          className={cn(
            'rounded-adipa-md text-adipa-text-secondary inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center justify-self-end',
            'duration-adipa-fast hover:bg-adipa-error-surface hover:text-adipa-error transition-colors',
            'active:text-adipa-error active:scale-90',
            'focus-visible:outline-adipa-primary focus-visible:outline-2 focus-visible:outline-offset-1',
          )}
          type="button"
          onClick={onRemove}
        >
          <TrashSimpleIcon size={15} weight="regular" />
        </button>
      ) : (
        <span aria-hidden className={small ? 'hidden' : 'block'} />
      )}
    </article>
  );
}
